// Client-side image downscale + re-encode before upload.
//
// Banner / product / section images are uploaded raw today, so a 3-4 MB phone
// photo spends hundreds of ms (sometimes seconds) in the request-body upload
// ("Request sent" in the Network panel) on a typical upstream connection.
// Re-encoding to a sane max dimension + JPEG quality on the client shrinks that
// to ~200-400 KB with no visible quality loss for a full-width banner.
//
// Pure browser canvas - no extra dependency. Any failure (undecodable file,
// no canvas context, result not actually smaller) falls back to the original
// file so an upload is never blocked by this.

export interface CompressImageOptions {
  /** Longest edge the output may have, in px. Aspect ratio is preserved. */
  maxWidth?: number;
  maxHeight?: number;
  /** Encoder quality for lossy types, 0-1. */
  quality?: number;
  /** Output MIME type. Defaults to image/jpeg. */
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
  /** Files already at or below this size (bytes) are returned untouched. */
  skipIfUnder?: number;
}

const DEFAULTS: Required<CompressImageOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.72,
  mimeType: "image/jpeg",
  skipIfUnder: 150 * 1024,
};

// Vector / animated formats can't be meaningfully re-encoded this way.
const SKIP_TYPES = new Set(["image/gif", "image/svg+xml", "image/avif"]);

export async function compressImage(
  file: File,
  options: CompressImageOptions = {},
): Promise<File> {
  const opts = { ...DEFAULTS, ...options };

  if (!file.type.startsWith("image/") || SKIP_TYPES.has(file.type)) {
    return file;
  }
  if (file.size <= opts.skipIfUnder) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    // "from-image" honours EXIF orientation (e.g. phone photos). It's a valid
    // ImageBitmapOptions value but predates this TS lib.dom, hence the cast.
    bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    } as unknown as ImageBitmapOptions);
  } catch {
    return file;
  }

  try {
    const scale = Math.min(
      1,
      opts.maxWidth / bitmap.width,
      opts.maxHeight / bitmap.height,
    );
    const targetW = Math.max(1, Math.round(bitmap.width * scale));
    const targetH = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    // JPEG has no alpha - matte transparent pixels onto white so they
    // don't render as black.
    if (opts.mimeType === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetW, targetH);
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, opts.mimeType, opts.quality),
    );

    if (!blob || blob.size >= file.size) {
      return file;
    }

    const ext = opts.mimeType === "image/png" ? "png" : opts.mimeType === "image/webp" ? "webp" : "jpg";
    const baseName = file.name.replace(/\.[^./\\]+$/, "") || "image";
    return new File([blob], `${baseName}.${ext}`, {
      type: opts.mimeType,
      lastModified: Date.now(),
    });
  } finally {
    bitmap.close?.();
  }
}
